import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Select, Tag, Space, Statistic, Row, Col, Card,
  message, Modal, Progress,
} from 'antd';
import {
  PlusOutlined, EyeOutlined, ReconciliationOutlined,
  DollarOutlined, ClockCircleOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractApi, getApiBaseURL } from '../../services/api';
import dayjs from 'dayjs';

const CONTRACT_TYPES = ['产品销售合同', '工程合同', '运维合同', '框架协议'];
const CONTRACT_STATUSES = ['草稿', '审批中', '驳回', '已签署', '执行中', '已完成', '已终止'];

const STATUS_COLORS = {
  '草稿': 'default',
  '审批中': 'blue',
  '驳回': 'red',
  '已签署': 'green',
  '执行中': 'processing',
  '已完成': 'success',
  '已终止': 'error',
};

export default function Contracts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQuotation = searchParams.get('fromQuotation');

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ page: 1, pageSize: 10, status: '', customerId: '', type: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: query.page, pageSize: query.pageSize };
      if (query.status) params.status = query.status;
      if (query.customerId) params.customerId = query.customerId;
      if (query.type) params.type = query.type;
      const res = await contractApi.list(params);
      setData(res.data);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 统计计算
  const totalAmount = data.reduce((s, c) => s + (c.totalAmount || 0), 0);
  const totalPaid = data.reduce((s, c) => s + (c.paidAmount || 0), 0);
  const unpaid = totalAmount - totalPaid;

  // 即将到期（30天内）
  const now = dayjs();
  const expiringSoon = data.filter((c) => {
    if (!c.endAt) return false;
    const end = dayjs(c.endAt);
    const diff = end.diff(now, 'day');
    return diff >= 0 && diff <= 30 && c.status !== '已终止' && c.status !== '已完成';
  }).length;

  const columns = [
    {
      title: '合同编号', dataIndex: 'code', key: 'code', width: 140,
      render: (code, r) => <a onClick={() => navigate(`/contracts/${r.id}`)}>{code}</a>,
    },
    {
      title: '客户', key: 'customer', width: 140,
      render: (_, r) => r.customer?.name || '—',
    },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 120,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: '合同总额', dataIndex: 'totalAmount', key: 'totalAmount', width: 140,
      render: (v) => v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' }),
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '回款进度', key: 'paymentProgress', width: 170,
      render: (_, r) => {
        const paid = r.paidAmount || 0;
        const pct = r.totalAmount > 0 ? Math.round((paid / r.totalAmount) * 100) : 0;
        return (
          <div>
            <Progress percent={pct} size="small" style={{ margin: 0 }}
              format={() => `${pct}%`} />
            <span style={{ fontSize: 12, color: '#888' }}>
              {paid.toLocaleString()}/{r.totalAmount.toLocaleString()}
            </span>
          </div>
        );
      },
    },
    {
      title: '创建日期', dataIndex: 'createdAt', key: 'createdAt', width: 110,
      render: (v) => dayjs(v).format('YYYY-MM-DD'),
    },
    {
      title: '到期日期', key: 'endAt', width: 110,
      render: (_, r) => {
        if (!r.endAt) return <span style={{ color: '#999' }}>—</span>;
        const end = dayjs(r.endAt);
        const diff = end.diff(now, 'day');
        const isExpiring = diff >= 0 && diff <= 30 && r.status !== '已终止' && r.status !== '已完成';
        return (
          <span className={isExpiring ? 'expiring-date' : ''}>
            {end.format('YYYY-MM-DD')}
          </span>
        );
      },
    },
    {
      title: '操作', key: 'actions', width: 120,
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />}
          onClick={() => navigate(`/contracts/${r.id}`)}>查看</Button>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="合同总额" value={totalAmount} precision={0}
              prefix={<ReconciliationOutlined />} suffix="CNY" />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="已回款" value={totalPaid} precision={0}
              prefix={<DollarOutlined />} suffix="CNY"
              valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="待回款" value={unpaid} precision={0}
              prefix={<DollarOutlined />} suffix="CNY"
              valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="即将到期" value={expiringSoon}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: expiringSoon > 0 ? '#faad14' : undefined }}
              suffix="份" />
          </Card>
        </Col>
      </Row>

      {/* 工具栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            placeholder="筛选状态" allowClear style={{ width: 140 }}
            value={query.status || undefined}
            onChange={(v) => setQuery((q) => ({ ...q, status: v || '', page: 1 }))}
          >
            {CONTRACT_STATUSES.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder="合同类型" allowClear style={{ width: 140 }}
            value={query.type || undefined}
            onChange={(v) => setQuery((q) => ({ ...q, type: v || '', page: 1 }))}
          >
            {CONTRACT_TYPES.map((t) => (
              <Select.Option key={t} value={t}>{t}</Select.Option>
            ))}
          </Select>
        </Space>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => window.open(getApiBaseURL() + '/exports/contracts', '_blank')}>导出 CSV</Button>
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => navigate(fromQuotation ? `/contracts/new?fromQuotation=${fromQuotation}` : '/contracts/new')}
            style={{ background: '#07c160', borderColor: '#07c160' }}>
            新建合同
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowClassName={(record) => {
          if (!record.endAt) return '';
          const end = dayjs(record.endAt);
          const diff = end.diff(now, 'day');
          if (diff >= 0 && diff <= 30 && record.status !== '已终止' && record.status !== '已完成') {
            return 'row-expiring-soon';
          }
          return '';
        }}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 份合同`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />
    </div>
  );
}
